/** ModalComponent: reusable full-screen modal, opened by data-modal-trigger and closed by data-modal-close, backdrop or Escape; mirrors DrawerComponent's API and handling, differs only visually, and opens positioned on [data-modal-scroll-anchor] when the content declares one. */
class ModalComponent extends HTMLElement {
  connectedCallback() {
    this._backdrop = this.querySelector("[data-modal-backdrop]");
    this._panel = this.querySelector("[data-modal-panel]");

    // Fermée au départ : le panneau est retiré de l'ordre de tabulation et des
    // lecteurs d'écran tant qu'elle n'est pas ouverte (`inert`).
    if (!this.isOpen && this._panel) this._panel.inert = true;

    this._backdrop?.addEventListener("click", () => this.close());

    this.querySelectorAll("[data-modal-close]").forEach((btn) => {
      btn.addEventListener("click", () => this.close());
    });

    this._onKeydown = (e) => {
      if (e.key === "Escape") this.close();
    };

    this._onDocClick = (e) => {
      const trigger = e.target.closest("[data-modal-trigger]");
      if (trigger && trigger.dataset.modalTrigger === this.id) {
        this._triggerEl = trigger;
        this.open();
      }
    };

    document.addEventListener("click", this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onDocClick);
    document.removeEventListener("keydown", this._onKeydown);
  }

  open() {
    this.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    if (this._panel) {
      this._panel.inert = false;
      // Par défaut on rouvre en haut ; un ancrage éventuel corrige juste après.
      this._panel.scrollTop = 0;
      this._scrollToAnchor();
    }
    document.addEventListener("keydown", this._onKeydown);

    // Focus sur le premier élément focusable (souvent le bouton de fermeture).
    requestAnimationFrame(() => {
      const focusable = this._panel?.querySelector(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    });

    this.dispatchEvent(new CustomEvent("modal:open", { bubbles: true }));
  }

  /** Positions the panel on [data-modal-scroll-anchor] when the content declares one, measured by rect rather than offsetTop since the anchor's offsetParent depends on the content. */
  _scrollToAnchor() {
    const panel = this._panel;
    const anchor = panel.querySelector("[data-modal-scroll-anchor]");
    if (!anchor) return;

    // -1 tant qu'aucun positionnement n'a eu lieu.
    let applied = -1;

    const apply = () => {
      // L'utilisateur a repris la main sur le défilement : on ne le bouscule pas.
      if (applied !== -1 && panel.scrollTop !== applied) return;
      panel.scrollTop =
        anchor.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop;
      applied = panel.scrollTop;
    };

    apply();

    // Images carry width/height so their height is reserved and the measure above is already right; this only re-aligns if one of them shifts the layout anyway.
    panel.querySelectorAll("img:not([src=''])").forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", apply, { once: true });
    });
  }

  close() {
    this.removeAttribute("data-open");
    document.body.style.overflow = "";
    if (this._panel) this._panel.inert = true;
    document.removeEventListener("keydown", this._onKeydown);

    // Rend le focus au bouton déclencheur.
    this._triggerEl?.focus();
    this._triggerEl = null;

    this.dispatchEvent(new CustomEvent("modal:close", { bubbles: true }));
  }

  get isOpen() {
    return this.hasAttribute("data-open");
  }
}

if (!customElements.get("modal-component")) {
  customElements.define("modal-component", ModalComponent);
}
